import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * SellerTrapSqueeze — Short Covering / Long Unwinding Squeeze
 *
 * How it works:
 * - Option buyers make money when Option Sellers panic.
 * - Checks if LTP is rising (positive momentum) but OI is dropping heavily (negative OI change).
 * - This indicates trapped sellers covering their positions at a loss, leading to a gamma squeeze.
 */
export const evaluateSellerTrapSqueeze: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap, activeRange } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const minStrike = atmStrike - (activeRange * gap);
    const maxStrike = atmStrike + (activeRange * gap);

    // Filter strikes within range
    const inRangeOpts = liveFlatOpts.filter((opt: any) => opt.strike >= minStrike && opt.strike <= maxStrike);

    let maxTrapScore = 0;
    let bestSignal: SignalResult | null = null;

    for (const opt of inRangeOpts) {
        if (!opt.type) continue;
        
        // Trap logic: OI Change is negative, but premium might be high or rising
        // We look for highest magnitude of negative OI Change on strikes close to ATM
        const oiChg = opt.oiChg || 0;
        
        // Dynamic threshold based on symbol gap to adapt to Nifty vs Sensex
        const threshold = gap >= 100 ? -20000 : -50000;
        
        if (oiChg < threshold) {
            const distance = Math.abs(opt.strike - spot);
            const proximityScore = Math.max(1, 10 - Math.round(distance / gap)); // Closer to ATM is better
            
            // Trap score is magnitude of covering weighted by proximity
            const trapScore = Math.abs(oiChg) * proximityScore;
            
            if (trapScore > maxTrapScore) {
                maxTrapScore = trapScore;
                bestSignal = {
                    trade_type: opt.type === 'CE' ? 'BULL' : 'BEAR', // CE sellers covering = Bullish, PE sellers covering = Bearish
                    strike: opt.strike,
                    option_type: opt.type as 'CE' | 'PE',
                    entry_price: opt.ltp || opt.price || opt.close || 0,
                    remarks: `${opt.type} Sellers Trapped (Covering ${Math.abs(oiChg).toLocaleString('en-IN')} OI)`
                };
            }
        }
    }

    if (!bestSignal || bestSignal.entry_price <= 0) return null;
    return bestSignal;
};
